import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { RowActions } from "@/components/app/patterns/row-actions";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { readState } from "@/lib/query/read-state";
import { OneBuildingCard } from "@/components/app/workspace/one-building-card";
import { PagedRules } from "../-components/paged-rules";

export const Route = createFileRoute("/_authenticated/_front-desk/sarah/suppression/")({
  component: Suppression,
});

interface Rule {
  phone?: string | null;
  company?: string | null;
  note?: string | null;
}

const fail = (error: Error) => toast.error(errorWords(error));

/** Callers the agent screens out. */
function Suppression() {
  const { facilityId } = useFacility();
  const queryClient = useQueryClient();
  const realFacility = facilityId === "all" ? null : facilityId;
  /** Only a started edit lives in state. Until somebody changes something the table reads the
   * stored list, so a save elsewhere is not hidden behind a stale copy. */
  const [draft, setDraft] = useState<Rule[] | null>(null);

  const stored = useQuery({
    ...orpc.frontDesk.agent.suppression.queryOptions({
      input: { facilityId: realFacility ?? "" },
    }),
    enabled: Boolean(realFacility),
  });

  const rules = draft ?? (stored.data?.rules as Rule[] | undefined) ?? [];
  const setRules = (next: Rule[] | ((previous: Rule[]) => Rule[])) =>
    setDraft(typeof next === "function" ? next(rules) : next);

  const save = useMutation(
    orpc.frontDesk.agent.saveSuppression.mutationOptions({
      onSuccess: async (result) => {
        toast.success("Saved");
        // What was saved is now the stored list; drop the draft so the table
        // reflects what the server actually kept, including rules it dropped.
        setDraft(null);
        void result;
        await queryClient.invalidateQueries({ queryKey: orpc.frontDesk.agent.key() });
      },
      onError: fail,
    }),
  );

  if (!realFacility) {
    return (
      <>
        <PageHeader title="Screened calls" meta="Callers the agent screens out." />
        <OneBuildingCard prompt="Choose a building. Each one screens its own callers." />
      </>
    );
  }

  const addRule = () => setRules((previous) => [...previous, { phone: "", company: "" }]);
  const update = (index: number, patch: Partial<Rule>) =>
    setRules((previous) =>
      previous.map((rule, position) => (position === index ? { ...rule, ...patch } : rule)),
    );

  return (
    <>
      <PageHeader
        title="Screened calls"
        meta="Callers the agent screens out. They are recorded on the timeline; the caller never learns it."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={addRule}>
              Add a rule
            </Button>
            <Button
              disabled={save.isPending}
              onClick={() => save.mutate({ facilityId: realFacility, rules })}
            >
              Save
            </Button>
          </div>
        }
      />

      {stored.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : readState(stored) === "failed" ? (
        <ReadFailed
          message="Could not load the screening rules."
          error={stored.error}
          retrying={stored.isFetching}
          onRetry={() => void stored.refetch()}
        />
      ) : rules.length > 0 ? (
        <PagedRules rules={rules}>
          {(page) => (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Number</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Why</TableHead>
                  <TableHead className="text-right">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {page.map(({ rule, index }) => (
                  <TableRow
                    /* Position, not content: a key built from the number remounted the
                   row on every keystroke, and the field lost focus after one digit.
                   Every cell is controlled, so a removed row cannot leave state behind. */
                    // oxlint-disable-next-line no-array-index-key
                    key={index}
                  >
                    <TableCell>
                      <Input
                        value={rule.phone ?? ""}
                        placeholder="+1 512 555 0100"
                        aria-label="Number"
                        onChange={(event) => update(index, { phone: event.target.value })}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={rule.company ?? ""}
                        placeholder="Acme Marketing"
                        aria-label="Company"
                        onChange={(event) => update(index, { company: event.target.value })}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={rule.note ?? ""}
                        aria-label="Why"
                        onChange={(event) => update(index, { note: event.target.value })}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <RowActions
                        label={rule.phone || "this rule"}
                        items={[
                          {
                            label: "Remove",
                            destructive: true,
                            onSelect: () =>
                              setRules((previous) =>
                                previous.filter((_, position) => position !== index),
                              ),
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </PagedRules>
      ) : (
        <EmptyState
          title="Nobody screened"
          description="Every caller reaches the building until a rule says otherwise."
        />
      )}

      <p className="text-muted-foreground text-sm">
        A rule with neither a number nor a company matches nobody, and is dropped when you save.
      </p>
    </>
  );
}
